import React, {useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {IconName} from '@lightricks/react-design-system';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import PERMISSIONS from '@/config/permissions';
import useAcceptCreator from '@/views/campaigns/creators-tab/hooks/use-accept-creator/useAcceptCreator';
import {getActionConfig} from '@/views/campaigns/creators-tab/types/creatorActionConfig';
import {CREATOR_STATUS} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import type {
  CampaignCreatorCounts,
  CampaignCreatorRow,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import checkPendingNegotiation from '@/views/campaigns/creators-tab/utils/hasPendingNegotiation';
import {
  CREATOR_ACTION_KEYS,
  type CreatorActionKey,
} from '@/views/creators/components/creator-action/CreatorAction';
import FlashMessage from '@/components/flash-message';
import useCreateBrandSafetyVetting from '@/hooks/mutations/brand-safety/use-create-brand-safety-vetting';
import useRegenerateBrandSafetyReport from '@/hooks/mutations/brand-safety/use-regenerate-brand-safety-report';
import useRejectProposal from '@/hooks/mutations/use-reject-proposal';
import useShortlist from '@/hooks/mutations/use-shortlist-update';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import useSubscriptionQuery from '@/hooks/queries/use-subscription-query';
import useBrandId from '@/hooks/use-brand-id';
import useDashboardUserPermissions from '@/hooks/use-dashboard-user-permissions';

const VETTING_TYPE_ON_PLATFORM = 'on_platform';

export type MenuItemConfig = {
  id: string;
  label: string;
  icon?: IconName;
  visible: boolean;
  disabled: boolean;
  tooltip?: string;
  onClick: () => void;
};

interface UseCreatorRowMenuItemsParams {
  creator: CampaignCreatorRow;
  onActionComplete: (countAdjustment?: Partial<CampaignCreatorCounts>) => void;
  onClose: () => void;
  onSetActiveAction: (action: CreatorActionKey) => void;
  onNegotiate: () => void;
  onOpenCancelModal: () => void;
  onOpenDueDateModal: () => void;
  onOpenCompleteModal: () => void;
  onWithdraw: () => void;
}

function useCreatorRowMenuItems({
  creator,
  onActionComplete,
  onClose,
  onSetActiveAction,
  onNegotiate,
  onOpenCancelModal,
  onOpenDueDateModal,
  onOpenCompleteModal,
  onWithdraw,
}: UseCreatorRowMenuItemsParams): MenuItemConfig[] {
  const {campaignId} = useParams<{campaignId: string}>();
  const brandId = useBrandId();
  const {campaign} = useCampaignQuery({campaignId});
  const {allowEditCreatorBid} = useAgencyQuery({brandId: brandId ?? ''});
  const {subscription} = useSubscriptionQuery({brandId: brandId ?? ''});
  const {acceptCreator, isAccepting} = useAcceptCreator(onActionComplete);
  const rejectProposal = useRejectProposal();
  const createVetting = useCreateBrandSafetyVetting({brandId});
  const regenerateReport = useRegenerateBrandSafetyReport({brandId});
  // Stable single-element array so useShortlist's internal memos don't
  // recompute on every render due to a new array reference.
  const creatorList = useMemo(() => [creator], [creator]);
  const shortlist = useShortlist(campaignId, creatorList);
  // Missing permission keys read as undefined and gate the action off —
  // safer default than implicit allow.
  const contractPermissions = useDashboardUserPermissions([
    PERMISSIONS.CONTRACT.UPDATE,
    PERMISSIONS.CONTRACT.COMPLETE,
    PERMISSIONS.CONTRACT.CANCEL,
  ]);

  const isApplicationCreator =
    !!creator.paidGigId &&
    creator.applicantStatus === CREATOR_STATUS.Applications;
  const isDeclined = creator.applicantStatus === CREATOR_STATUS.Declined;
  const isHired = creator.applicantStatus === CREATOR_STATUS.Hired;
  const hasPendingNegotiation = checkPendingNegotiation(creator);
  const isNegotiable =
    !campaign?.hideInfluencerPricing &&
    !!allowEditCreatorBid &&
    !hasPendingNegotiation;

  const needsCompletion =
    creator.acceptedAssetsCount != null &&
    creator.requiredAssetsCount != null &&
    creator.acceptedAssetsCount < creator.requiredAssetsCount;
  // v1's showCancelContract requires totalContentSubmissionCount === 0; the
  // closest faithful proxy in v2 without a backend change is the aasm_state
  // before any upload has happened.
  const hasNoUploadsYet = creator.aasmState === 'awaiting_influencer_uploads';
  const isNotInTerminalState =
    creator.aasmState !== 'completed' &&
    creator.aasmState !== 'awaiting_payment';
  const canNegotiateHired =
    isHired &&
    !!campaign?.isOpenMarket &&
    !subscription?.isSelfServe &&
    needsCompletion &&
    isNotInTerminalState &&
    !!allowEditCreatorBid &&
    !campaign?.hideInfluencerPricing &&
    !!contractPermissions.completeContract;

  function handleAccept() {
    onClose();
    acceptCreator(creator);
  }

  function handleReject() {
    onClose();
    if (!creator.paidGigId) return;

    rejectProposal.mutate(
      {paidGigId: creator.paidGigId},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            mode: 'light',
            message: (
              <FlashMessage.Banner
                title={translate('Creator rejected')}
                subtitle={creator.creatorName}
              />
            ),
          });
          onActionComplete();
        },
        onError: (error: unknown) => {
          raiseFlashMessageError({
            title: translate('Failed to reject creator'),
            subtitle: extractBackendErrorDetail(error),
          });
          onActionComplete();
        },
      }
    );
  }

  function handleVet() {
    onClose();
    if (creator.vettingId) {
      regenerateReport.mutate(
        {vettingId: creator.vettingId},
        {
          onSuccess: () => {
            raiseFlashMessage({
              status: 'success',
              mode: 'light',
              message: (
                <FlashMessage.Banner
                  title={translate('Re-vetting initiated')}
                  subtitle={creator.creatorName}
                />
              ),
            });
            onActionComplete();
          },
          onError: () => {
            raiseFlashMessageError();
          },
        }
      );
    } else {
      createVetting.mutate(
        {
          creator: {
            id: creator.profileId,
            fullName: creator.creatorName,
            email: null,
            emails: null,
          },
          brandId,
          type: VETTING_TYPE_ON_PLATFORM,
        },
        {
          onSuccess: () => {
            raiseFlashMessage({
              status: 'success',
              mode: 'light',
              message: (
                <FlashMessage.Banner
                  title={translate('Vetting initiated')}
                  subtitle={creator.creatorName}
                />
              ),
            });
            onActionComplete();
          },
          onError: () => {
            raiseFlashMessageError();
          },
        }
      );
    }
  }

  function handleAddToList() {
    onClose();
    onSetActiveAction(CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS);
  }

  function handleShare() {
    onClose();
    onSetActiveAction(CREATOR_ACTION_KEYS.CREATE_AND_SHARE_LIST);
  }

  function handleNegotiate() {
    onClose();
    onNegotiate();
  }

  function handleRemove() {
    onClose();
    onOpenCancelModal();
  }

  function handleChangeDueDate() {
    onClose();
    onOpenDueDateModal();
  }

  function handleMoveToComplete() {
    onClose();
    onOpenCompleteModal();
  }

  function handleWithdraw() {
    onClose();
    onWithdraw();
  }

  function handleToggleShortlist() {
    onClose();
    const wasShortlisted = shortlist.isShortlisted(creator.id);
    shortlist.toggle(creator.id);
    raiseFlashMessage({
      status: 'success',
      mode: 'light',
      message: (
        <FlashMessage.Banner
          title={
            wasShortlisted
              ? translate('Removed from shortlist')
              : translate('Added to shortlist')
          }
          subtitle={creator.creatorName}
        />
      ),
    });
  }

  const isCreatorShortlisted = shortlist.isShortlisted(creator.id);

  return [
    {
      id: 'accept',
      label: translate(getActionConfig('accept')?.label ?? 'Accept'),
      icon: getActionConfig('accept')?.icon,
      visible: isApplicationCreator,
      disabled: isAccepting || hasPendingNegotiation,
      tooltip: hasPendingNegotiation
        ? translate(
            'Please resolve the outstanding offer prior to accepting this creator application.'
          )
        : undefined,
      onClick: handleAccept,
    },
    {
      id: 'reject',
      label: translate(getActionConfig('reject')?.label ?? 'Reject'),
      icon: getActionConfig('reject')?.icon,
      visible: isApplicationCreator,
      disabled: rejectProposal.isLoading,
      onClick: handleReject,
    },
    {
      id: 'negotiate',
      label: translate(getActionConfig('negotiate')?.label ?? 'Negotiate'),
      icon: getActionConfig('negotiate')?.icon,
      visible:
        (isApplicationCreator && isNegotiable) ||
        (canNegotiateHired && !hasPendingNegotiation),
      disabled: false,
      onClick: handleNegotiate,
    },
    {
      id: 'withdraw',
      label: translate(getActionConfig('withdraw')?.label ?? 'Withdraw Offer'),
      icon: getActionConfig('withdraw')?.icon,
      visible:
        hasPendingNegotiation &&
        !!creator.brandNegotiationSnapshot &&
        ((isApplicationCreator &&
          !campaign?.hideInfluencerPricing &&
          !!allowEditCreatorBid) ||
          canNegotiateHired),
      disabled: false,
      onClick: handleWithdraw,
    },
    {
      id: 'vet',
      label: translate(getActionConfig('vet')?.label ?? 'Vet'),
      icon: getActionConfig('vet')?.icon,
      visible: !isDeclined,
      disabled: createVetting.isLoading || regenerateReport.isLoading,
      onClick: handleVet,
    },
    {
      id: 'add-to-shortlist',
      label: isCreatorShortlisted
        ? translate('Remove from shortlist')
        : translate('Add to shortlist'),
      icon: isCreatorShortlisted
        ? 'Navigation-Heart-Fill'
        : 'Navigation-Heart-Line',
      visible: isApplicationCreator,
      disabled: false,
      onClick: handleToggleShortlist,
    },
    {
      id: 'change-due-date',
      label: translate(
        getActionConfig('change-due-date')?.label ?? 'Change due date'
      ),
      icon: getActionConfig('change-due-date')?.icon,
      visible: isHired && !!contractPermissions.updateContract,
      disabled: false,
      onClick: handleChangeDueDate,
    },
    {
      id: 'move-to-complete',
      label: translate(
        getActionConfig('move-to-complete')?.label ?? 'Move to complete'
      ),
      icon: getActionConfig('move-to-complete')?.icon,
      visible:
        isHired && !!contractPermissions.completeContract && needsCompletion,
      disabled: hasPendingNegotiation,
      tooltip: hasPendingNegotiation
        ? translate('Pending offer exists')
        : undefined,
      onClick: handleMoveToComplete,
    },
    {
      id: 'remove',
      label: translate(getActionConfig('remove')?.label ?? 'Remove'),
      icon: getActionConfig('remove')?.icon,
      visible:
        isHired && !!contractPermissions.cancelContract && hasNoUploadsYet,
      disabled: false,
      onClick: handleRemove,
    },
    {
      id: 'add-to-list',
      label: translate(getActionConfig('add-to-list')?.label ?? 'Add to List'),
      icon: getActionConfig('add-to-list')?.icon,
      visible: true,
      disabled: false,
      onClick: handleAddToList,
    },
    {
      id: 'share',
      label: translate(getActionConfig('share')?.label ?? 'Share'),
      icon: getActionConfig('share')?.icon,
      visible: true,
      disabled: false,
      onClick: handleShare,
    },
  ];
}

export default useCreatorRowMenuItems;
